// src/redux/slices/securitySlice.js
// Unified Security Redux Slice - RTK Migration
// Replaces both securityReducer.js and old partial securitySlice.js
// State structure: byAssessment instead of byModel

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { authFetch } from '../../utils/authFetch';

// ============================================================================
// Initial State
// ============================================================================
const initialState = {
  byAssessment: {},
  loading: false,
  saving: false,
  error: null,
  saveError: null,
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Ensure byAssessment[assessmentUuid] structure exists
 */
const ensureAssessmentStructure = (state, assessmentUuid) => {
  if (!state.byAssessment[assessmentUuid]) {
    state.byAssessment[assessmentUuid] = {
      assessmentData: {},
      activeModelId: null,
      scenarios: {},
      risks: {},
      controls: {},
    };
  }
};

// ============================================================================
// Async Thunks - Assessment Operations
// ============================================================================

/**
 * Fetch security assessment data
 */
export const fetchSecurityAssessment = createAsyncThunk(
  'security/fetchSecurityAssessment',
  async ({ modelId, assessmentUuid }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/fetchSecurityAssessment', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch security assessment: ${response.status}`);
      }

      const data = await response.json();
      return { assessmentUuid, modelId, assessmentData: data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// Async Thunks - Scenario Operations
// ============================================================================

/**
 * Fetch all scenarios for an assessment
 */
export const fetchScenarios = createAsyncThunk(
  'security/fetchScenarios',
  async ({ modelId, assessmentUuid }, { rejectWithValue }) => {
    try {
      console.log('[securitySlice] fetchScenarios', { modelId, assessmentUuid });

      const response = await authFetch('/.netlify/functions/fetchScenarios', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch scenarios: ${response.status}`);
      }

      const data = await response.json();
      const scenarios = data.scenarios || [];
      console.log('[securitySlice] fetchScenarios success', scenarios.length);

      return { assessmentUuid, modelId, scenarios };
    } catch (error) {
      console.error('[securitySlice] fetchScenarios error:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Save/update a scenario
 */
export const saveScenario = createAsyncThunk(
  'security/saveScenario',
  async (
    { modelId, scenarioId, name, mappedAgents, attributes, assessmentUuid, attackSurface, impact, status },
    { rejectWithValue }
  ) => {
    try {
      const response = await authFetch('/.netlify/functions/saveScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          modelId,
          scenarioId,
          name,
          mappedAgents,
          attributes,
          assessmentUuid,
          attackSurface,
          impact,
          status,
        }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save scenario: ${response.status}`);
      }

      const data = await response.json();
      return { assessmentUuid, scenario: data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Delete a scenario
 */
export const deleteScenario = createAsyncThunk(
  'security/deleteScenario',
  async ({ modelId, scenarioId, assessmentUuid, cascade = false }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/deleteScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, scenarioId, cascade }),
      });

      if (!response.ok) {
        throw new Error(`Failed to delete scenario: ${response.status}`);
      }

      return { assessmentUuid, scenarioId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Request AI-generated scenario name
 */
export const requestScenarioName = createAsyncThunk(
  'security/requestScenarioName',
  async ({ modelId, scenarioId, assessmentUuid }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const scenario = state.security.byAssessment[assessmentUuid]?.scenarios[scenarioId];

      if (!scenario) {
        throw new Error('Scenario not found');
      }

      const response = await authFetch('/.netlify/functions/generateScenarioName', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, scenario }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate scenario name: ${response.status}`);
      }

      const { suggestedName } = await response.json();
      return { assessmentUuid, scenarioId, suggestedName };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Persist scenario name
 */
export const persistScenarioName = createAsyncThunk(
  'security/persistScenarioName',
  async ({ modelId, assessmentUuid, scenarioId, name }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/saveScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, scenarioId, assessmentUuid, name }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update scenario name: ${response.status}`);
      }

      return { assessmentUuid, scenarioId, name };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Persist scenario category
 */
export const persistScenarioCategory = createAsyncThunk(
  'security/persistScenarioCategory',
  async ({ modelId, assessmentUuid, scenarioId, category }, { getState, rejectWithValue }) => {
    try {
      const existingScenario = getState().security.byAssessment[assessmentUuid]?.scenarios[scenarioId];
      const prevAttributes = existingScenario?.attributes || {};
      const nextAttributes = { ...prevAttributes, 'Threat Category': category };

      const response = await authFetch('/.netlify/functions/saveScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, scenarioId, assessmentUuid, attributes: nextAttributes }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update scenario category: ${response.status}`);
      }

      return { assessmentUuid, scenarioId, attributes: nextAttributes };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Persist scenario agents/actors
 */
export const persistScenarioAgents = createAsyncThunk(
  'security/persistScenarioAgents',
  async ({ modelId, assessmentUuid, scenarioId, mappedAgents }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/saveScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, scenarioId, assessmentUuid, mappedAgents }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update scenario agents: ${response.status}`);
      }

      return { assessmentUuid, scenarioId, mappedAgents };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Persist scenario impact (CIA triad)
 */
export const persistScenarioImpact = createAsyncThunk(
  'security/persistScenarioImpact',
  async ({ modelId, assessmentUuid, scenarioId, impactUpdates }, { getState, rejectWithValue }) => {
    try {
      const existingScenario = getState().security.byAssessment[assessmentUuid]?.scenarios[scenarioId];
      const prevImpact = existingScenario?.impact || {};
      const nextImpact = { ...prevImpact, ...impactUpdates };

      const response = await authFetch('/.netlify/functions/saveScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, scenarioId, assessmentUuid, impact: nextImpact }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update scenario impact: ${response.status}`);
      }

      return { assessmentUuid, scenarioId, impact: nextImpact };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Persist scenario attack surface
 */
export const persistScenarioAttackSurface = createAsyncThunk(
  'security/persistScenarioAttackSurface',
  async ({ modelId, assessmentUuid, scenarioId, attackType, rowData }, { getState, rejectWithValue }) => {
    try {
      const existingScenario = getState().security.byAssessment[assessmentUuid]?.scenarios[scenarioId];
      const prevAttackSurface = existingScenario?.attackSurface || {};
      const nextAttackSurface = { ...prevAttackSurface, [attackType]: rowData };

      const response = await authFetch('/.netlify/functions/saveScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, scenarioId, assessmentUuid, attackSurface: nextAttackSurface }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update scenario attack surface: ${response.status}`);
      }

      return { assessmentUuid, scenarioId, attackSurface: nextAttackSurface };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Persist generic scenario attribute
 */
export const persistScenarioAttribute = createAsyncThunk(
  'security/persistScenarioAttribute',
  async ({ modelId, assessmentUuid, scenarioId, attributeName, attributeValue }, { getState, rejectWithValue }) => {
    try {
      const existingScenario = getState().security.byAssessment[assessmentUuid]?.scenarios[scenarioId];
      const prevAttributes = existingScenario?.attributes || {};
      const nextAttributes = { ...prevAttributes, [attributeName]: attributeValue };

      const response = await authFetch('/.netlify/functions/saveScenario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, scenarioId, assessmentUuid, attributes: nextAttributes }),
      });

      if (!response.ok) {
        throw new Error(`Failed to update scenario attribute ${attributeName}: ${response.status}`);
      }

      return { assessmentUuid, scenarioId, attributes: nextAttributes };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Continued in next message due to length...
// ============================================================================
// Async Thunks - Risk Operations  
// ============================================================================

/**
 * Generate security risks using AI
 */
export const generateSecurityRisks = createAsyncThunk(
  'security/generateSecurityRisks',
  async ({ modelId, assessmentUuid, assessmentData, securityRiskCatalogue, riskArea }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/generateSecurityRisks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid, assessmentData, securityRiskCatalogue, riskArea }),
      });

      if (!response.ok) {
        throw new Error(`Error generating security risks: ${response.status}`);
      }

      const securityRisks = await response.json();
      return { assessmentUuid, securityRisks };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch security risks
 */
export const fetchSecurityRisks = createAsyncThunk(
  'security/fetchSecurityRisks',
  async ({ modelId, assessmentUuid, allowGeneration = false }, { dispatch, rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/fetchSecurityRisks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch security risks: ${response.status}`);
      }

      const risks = await response.json();

      // Auto-generate if allowed and no risks found
      if (allowGeneration && (!risks || risks.length === 0)) {
        await dispatch(generateSecurityRisks({ modelId, assessmentUuid }));
      }

      return { assessmentUuid, risks };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Save a security risk
 */
export const saveSecurityRisk = createAsyncThunk(
  'security/saveSecurityRisk',
  async ({ modelId, assessmentUuid, riskData }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/saveSecurityRisk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid, ...riskData }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save security risk: ${response.status}`);
      }

      const savedRisk = await response.json();
      return { assessmentUuid, risk: savedRisk };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Discard a security risk
 */
export const discardSecurityRisk = createAsyncThunk(
  'security/discardSecurityRisk',
  async ({ modelId, assessmentUuid, riskId }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/discardSecurityRisk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, riskId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to discard security risk: ${response.status}`);
      }

      return { assessmentUuid, riskId };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// Async Thunks - Control Operations
// ============================================================================

/**
 * Generate security controls using AI
 */
export const generateSecurityControls = createAsyncThunk(
  'security/generateSecurityControls',
  async ({ modelId, assessmentUuid, scenariosWithoutControls }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/generateSecurityControls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, assessmentUuid, scenarios: scenariosWithoutControls }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate security controls: ${response.status}`);
      }

      const controls = await response.json();
      return { assessmentUuid, controls };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Fetch security controls for a scenario
 */
export const fetchSecurityControls = createAsyncThunk(
  'security/fetchSecurityControls',
  async ({ modelId, assessmentUuid, scenarioId }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/fetchSecurityControls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ modelId, scenarioId }),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch security controls: ${response.status}`);
      }

      const data = await response.json();
      const controls = data.controls || [];
      return { assessmentUuid, scenarioId, controls };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Save a security control
 */
export const saveSecurityControl = createAsyncThunk(
  'security/saveSecurityControl',
  async ({ controlData, modelId, scenarioId, assessmentUuid }, { rejectWithValue }) => {
    try {
      const response = await authFetch('/.netlify/functions/saveSecurityControls', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ controlData, modelId, scenarioId, assessmentUuid }),
      });

      if (!response.ok) {
        throw new Error(`Failed to save security control: ${response.status}`);
      }

      const data = await response.json();
      return { assessmentUuid, scenarioId, control: data.control };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// ============================================================================
// Slice Definition
// ============================================================================
const securitySlice = createSlice({
  name: 'security',
  initialState,
  reducers: {
    // Reset entire security state
    resetSecurityState: () => initialState,

    // Set active model ID for an assessment
    setActiveModelId: (state, action) => {
      const { assessmentUuid, modelId } = action.payload;
      ensureAssessmentStructure(state, assessmentUuid);
      state.byAssessment[assessmentUuid].activeModelId = modelId;
    },

    // Load scenarios locally (from cache/initial load)
    loadScenarios: (state, action) => {
      const { assessmentUuid, scenarios } = action.payload;
      ensureAssessmentStructure(state, assessmentUuid);

      scenarios.forEach((scenario) => {
        const scenarioId = scenario.scenario_id || scenario.scenarioId;
        state.byAssessment[assessmentUuid].scenarios[scenarioId] = {
          scenarioId,
          name: scenario.name || scenario.threatScenario,
          mappedAgents: scenario.mapped_agents || scenario.mappedAgents || [],
          attributes: scenario.attributes || {},
          category: scenario.attributes?.['Threat Category'] || scenario.category || '',
          attackSurface: scenario.attack_surface || scenario.attackSurface || {},
          impact: scenario.impact || {},
          status: scenario.status || 'Draft',
          updatedAt: scenario.updatedAt || new Date().toISOString(),
        };
      });
    },

    // Clear assessment data
    clearAssessmentData: (state, action) => {
      const { assessmentUuid } = action.payload;
      delete state.byAssessment[assessmentUuid];
    },
  },

  extraReducers: (builder) => {
    // fetchScenarios
    builder
      .addCase(fetchScenarios.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchScenarios.fulfilled, (state, action) => {
        state.loading = false;
        const { assessmentUuid, scenarios } = action.payload;
        ensureAssessmentStructure(state, assessmentUuid);

        scenarios.forEach((scenario) => {
          const scenarioId = scenario.scenario_id || scenario.scenarioId;
          state.byAssessment[assessmentUuid].scenarios[scenarioId] = {
            scenarioId,
            name: scenario.name || scenario.threatScenario,
            mappedAgents: scenario.mapped_agents || scenario.mappedAgents || [],
            attributes: scenario.attributes || {},
            category: scenario.attributes?.['Threat Category'] || scenario.category || '',
            attackSurface: scenario.attack_surface || scenario.attackSurface || {},
            impact: scenario.impact || {},
            status: scenario.status || 'Draft',
            updatedAt: scenario.updatedAt || new Date().toISOString(),
            // Threat level fields from database
            threat_level: scenario.threat_level || null,
            threat_score: scenario.threat_score || null,
            vulnerability_score: scenario.vulnerability_score || null,
            impact_score: scenario.impact_score || null,
          };
        });
      })
      .addCase(fetchScenarios.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    // persistScenarioName
    builder
      .addCase(persistScenarioName.pending, (state) => {
        state.saving = true;
      })
      .addCase(persistScenarioName.fulfilled, (state, action) => {
        state.saving = false;
        const { assessmentUuid, scenarioId, name } = action.payload;
        if (state.byAssessment[assessmentUuid]?.scenarios[scenarioId]) {
          state.byAssessment[assessmentUuid].scenarios[scenarioId].name = name;
        }
      })
      .addCase(persistScenarioName.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload;
      });

    // persistScenarioImpact
    builder
      .addCase(persistScenarioImpact.pending, (state) => {
        state.saving = true;
      })
      .addCase(persistScenarioImpact.fulfilled, (state, action) => {
        state.saving = false;
        const { assessmentUuid, scenarioId, impact } = action.payload;
        if (state.byAssessment[assessmentUuid]?.scenarios[scenarioId]) {
          state.byAssessment[assessmentUuid].scenarios[scenarioId].impact = impact;
        }
      })
      .addCase(persistScenarioImpact.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload;
      });

    // persistScenarioAgents
    builder
      .addCase(persistScenarioAgents.pending, (state) => {
        state.saving = true;
      })
      .addCase(persistScenarioAgents.fulfilled, (state, action) => {
        state.saving = false;
        const { assessmentUuid, scenarioId, mappedAgents } = action.payload;
        if (state.byAssessment[assessmentUuid]?.scenarios[scenarioId]) {
          state.byAssessment[assessmentUuid].scenarios[scenarioId].mappedAgents = mappedAgents;
        }
      })
      .addCase(persistScenarioAgents.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload;
      });

    // persistScenarioAttribute
    builder
      .addCase(persistScenarioAttribute.pending, (state) => {
        state.saving = true;
      })
      .addCase(persistScenarioAttribute.fulfilled, (state, action) => {
        state.saving = false;
        const { assessmentUuid, scenarioId, attributes } = action.payload;
        if (state.byAssessment[assessmentUuid]?.scenarios[scenarioId]) {
          state.byAssessment[assessmentUuid].scenarios[scenarioId].attributes = attributes;
        }
      })
      .addCase(persistScenarioAttribute.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload;
      });

    // persistScenarioAttackSurface
    builder
      .addCase(persistScenarioAttackSurface.pending, (state) => {
        state.saving = true;
      })
      .addCase(persistScenarioAttackSurface.fulfilled, (state, action) => {
        state.saving = false;
        const { assessmentUuid, scenarioId, attackSurface } = action.payload;
        if (state.byAssessment[assessmentUuid]?.scenarios[scenarioId]) {
          state.byAssessment[assessmentUuid].scenarios[scenarioId].attackSurface = attackSurface;
        }
      })
      .addCase(persistScenarioAttackSurface.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload;
      });

    // saveSecurityControl
    builder
      .addCase(saveSecurityControl.pending, (state) => {
        state.saving = true;
      })
      .addCase(saveSecurityControl.fulfilled, (state, action) => {
        state.saving = false;
        const { assessmentUuid, scenarioId, control } = action.payload;
        ensureAssessmentStructure(state, assessmentUuid);
        
        const controlId = control.control_id || control.controlId;
        state.byAssessment[assessmentUuid].controls[controlId] = {
          ...control,
          scenarioId,
        };
      })
      .addCase(saveSecurityControl.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload;
      });

    // fetchSecurityControls
    builder
      .addCase(fetchSecurityControls.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchSecurityControls.fulfilled, (state, action) => {
        state.loading = false;
        const { assessmentUuid, scenarioId, controls } = action.payload;
        ensureAssessmentStructure(state, assessmentUuid);

        controls.forEach((control) => {
          const controlId = control.control_id || control.controlId;
          state.byAssessment[assessmentUuid].controls[controlId] = {
            ...control,
            scenarioId,
          };
        });
      })
      .addCase(fetchSecurityControls.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// ============================================================================
// Selectors
// ============================================================================

// Basic selectors
export const selectByAssessment = (state) => state.security.byAssessment;
export const selectLoading = (state) => state.security.loading;
export const selectSaving = (state) => state.security.saving;
export const selectError = (state) => state.security.error;

// Assessment-specific selectors
export const selectActiveModelId = (state, assessmentUuid) =>
  state.security.byAssessment[assessmentUuid]?.activeModelId || null;

// Scenario selectors
export const selectScenarios = (state, assessmentUuid) =>
  state.security.byAssessment[assessmentUuid]?.scenarios || {};

export const selectAllScenarios = (state, assessmentUuid) =>
  Object.values(state.security.byAssessment[assessmentUuid]?.scenarios || {});

export const selectScenarioById = (state, assessmentUuid, scenarioId) =>
  state.security.byAssessment[assessmentUuid]?.scenarios?.[scenarioId] || null;

export const selectScenarioCount = (state, assessmentUuid) =>
  Object.keys(state.security.byAssessment[assessmentUuid]?.scenarios || {}).length;

// Control selectors
// Returns controls grouped by scenarioId: { [scenarioId]: [...controls] }
export const selectControls = (state, assessmentUuid) => {
  const flatControls = state.security.byAssessment[assessmentUuid]?.controls || {};
  const grouped = {};

  Object.values(flatControls).forEach(control => {
    const scenarioId = control.scenarioId;
    if (!grouped[scenarioId]) {
      grouped[scenarioId] = [];
    }
    grouped[scenarioId].push(control);
  });

  return grouped;
};

export const selectControlsForScenario = (state, assessmentUuid, scenarioId) =>
  Object.values(state.security.byAssessment[assessmentUuid]?.controls || {})
    .filter(control => control.scenarioId === scenarioId);

// ============================================================================
// Exports
// ============================================================================
export const { resetSecurityState, setActiveModelId, loadScenarios, clearAssessmentData } =
  securitySlice.actions;

export default securitySlice.reducer;
